/**
 * Квести і бонуси — premium-сторінка в стилі Home/Profile (edge-to-edge).
 *
 * Структура:
 *   BackNav
 *   Hero (eyebrow + display heading з scribble underline)
 *   Balance hero — DARK featured card з сумою бонусів + CTA на /subscription
 *   Stats row — виконано / прогрес / можна заробити
 *   Group sections — Реферали / Підписки / AI
 *     Quest card з icon-tile, reward badge, progress bar, smart state
 */

import { useEffect, useMemo, useState } from "react";
import { MAX_BONUS_PERCENT } from "@/lib/bonuses";
import { Link, useSearchParams } from "react-router-dom";
import { BackNav } from "@/components/BackNav";
import { PageHeader } from "@/components/PageHeader";
import { Icon } from "@/components/Icon";
import type { IconName } from "@/components/Icon";
import { CenteredError, Spinner } from "@/components/Spinner";
import { useQuests, useSubscriptionQuests, useMarkHintSeen } from "@/api/hooks";
import { formatAmount } from "@/lib/utils";
import { SubscriptionQuestModal } from "@/components/SubscriptionQuestModal";
import type { QuestItemOut, SubscriptionQuestItemOut } from "@/api/types";

const GROUP_META: Record<
  string,
  { title: string; icon: IconName; subtitle: string }
> = {
  referrals: {
    title: "Реферали",
    icon: "users",
    subtitle: "Запрошуй друзів — отримуй бонуси",
  },
  subscription: {
    title: "Підписки",
    icon: "crown",
    subtitle: "Активуй та продовжуй підписку",
  },
  ai: {
    title: "AI-відповіді",
    icon: "brain",
    subtitle: "Генеруй відгуки на вакансії",
  },
  course: {
    title: "Курс із продажів",
    icon: "play",
    subtitle: "Пройди курс від засновника inFLOW",
  },
  reviews: {
    title: "Відгуки",
    icon: "star",
    subtitle: "Оцінюй замовників і розкажи про inFLOW",
  },
  lessons: {
    title: "База знань",
    icon: "book",
    subtitle: "Дивись уроки від спікерів",
  },
  saved: {
    title: "Збережені",
    icon: "bookmark",
    subtitle: "Складай вакансії, які не хочеш втратити",
  },
};

const GROUP_ORDER = [
  "referrals",
  "subscription",
  "ai",
  "reviews",
  "lessons",
  "saved",
  "course",
];

// Тап по квесту → відповідний розділ (юзер 2026-06-11): реферальні квести
// ведуть на /referrals (скопіювати посилання), підписка — на /subscription
// (оформити/продовжити), AI-відповіді — у стрічку вакансій.
const GROUP_ROUTE: Record<string, string> = {
  referrals: "/referrals",
  subscription: "/subscription",
  ai: "/feed",
  course: "/course",
  reviews: "/feed",
  lessons: "/knowledge-base",
  saved: "/saved",
};

function QuestCard({ q, groupIcon }: { q: QuestItemOut; groupIcon: IconName }) {
  const progress = q.progress ?? 0;
  const threshold = q.threshold;
  const hasProgress = threshold !== null && threshold !== undefined;
  const pct =
    hasProgress && threshold
      ? Math.min(100, Math.round((progress / threshold) * 100))
      : q.is_done
        ? 100
        : 0;

  return (
    <Link
      to={GROUP_ROUTE[q.group] ?? "/"}
      className={
        "block rounded-ios-card p-4 transition active:scale-[0.99] " +
        (q.is_done
          ? "bg-primary/[0.06] border border-primary/20"
          : "bg-surface shadow-card")
      }
    >
      <div className="flex items-start gap-3">
        <div
          className={
            "grid h-10 w-10 shrink-0 place-items-center rounded-[0.75rem] " +
            (q.is_done
              ? "bg-primary text-white"
              : "bg-primary/10 text-primary")
          }
        >
          {q.is_done ? (
            <Icon name="check" size={16} color="#FFFFFF" />
          ) : (
            <Icon name={groupIcon} size={16} />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="font-display text-[0.875rem] font-semibold text-ink leading-snug">
              {q.title}
            </div>
            <div
              className={
                "shrink-0 font-mono text-[0.75rem] font-bold whitespace-nowrap " +
                (q.is_done ? "text-primary" : "text-ink")
              }
            >
              +{formatAmount(q.reward_uah)} ₴
            </div>
          </div>

          {q.caption && (
            <div className="text-[0.75rem] text-ink-dim leading-relaxed mt-1 mb-2.5">
              {q.caption}
            </div>
          )}

          {/* Status row */}
          {q.is_done ? (
            <div className="font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-primary font-semibold flex items-center gap-1.5">
              <Icon name="check" size={12} color="#FE6B04" />
              Виконано · бонус нараховано
            </div>
          ) : hasProgress ? (
            <>
              <div className="h-1.5 w-full rounded-full bg-ink/5 overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-500 ease-out"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="mt-1.5 flex items-center justify-between font-mono text-[0.6875rem] text-ink-dim">
                <span>
                  {progress} / {threshold}
                </span>
                <span>{pct}%</span>
              </div>
            </>
          ) : (
            <div className="font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-ink-dim">
              Доступно після виконання
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

export function QuestsPage() {
  const query = useQuests();

  // Прихід із розсилки про нові квести (кнопка «Переглянути квести» веде на
  // /quests?announce=<ключ>): одразу позначаємо анонс переглянутим, щоб попап
  // «Додали нові квести» на головній цьому юзеру більше не показувався (owner
  // 2026-07-17). Тільки за параметром — звичайний захід у квести НЕ гасить
  // майбутні анонси.
  const [params] = useSearchParams();
  const markHint = useMarkHintSeen();
  const announceKey = params.get("announce");
  useEffect(() => {
    if (announceKey) markHint.mutate(announceKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [announceKey]);

  const computed = useMemo(() => {
    if (!query.data) return null;
    const items = query.data.items;
    const total = items.length;
    const done = items.filter((q) => q.is_done).length;
    const remainingReward = items
      .filter((q) => !q.is_done)
      .reduce((sum, q) => sum + q.reward_uah, 0);
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    const groups: Record<string, QuestItemOut[]> = {};
    for (const item of items) {
      if (!groups[item.group]) groups[item.group] = [];
      groups[item.group]!.push(item);
    }
    return { total, done, pct, remainingReward, groups };
  }, [query.data]);

  if (query.isLoading) return <Spinner />;
  if (query.isError) return <CenteredError message={query.error.message} />;

  const data = query.data!;
  const { total, done, pct, remainingReward, groups } = computed!;

  return (
    <div className="relative min-h-screen pb-16">
      {/* Warm aura — глобальна в Layout.tsx */}
      <div className="px-5 pt-5">
        <BackNav />
      </div>

      {/* Hero — єдиний PageHeader для всіх розділів (owner 2026-08-11). */}
      <div className="px-5">
        <PageHeader
          eyebrow="квести і бонуси"
          title="Виконуй — заробляй"
          titleLead="Виконуй —"
          titleAccent="заробляй."
          scribbleWidth={170}
        />
      </div>

      {/* Balance featured card (DARK) */}
      <div className="px-5 pt-4">
        <Link
          to="/subscription"
          className="relative flex items-center gap-3.5 overflow-hidden rounded-ios-hero bg-dark p-4 text-white shadow-ai-card"
        >
          <div
            className="pointer-events-none absolute -bottom-7 -right-7 h-[8.75rem] w-[8.75rem] rounded-full"
            style={{
              background:
                "radial-gradient(circle, rgba(255,161,53,0.40), rgba(255,161,53,0))",
            }}
          />
          <div className="relative grid h-11 w-11 place-items-center rounded-[0.875rem] bg-white text-primary">
            <Icon name="star" size={22} />
          </div>
          <div className="relative flex-1">
            <div className="font-mono text-[0.625rem] uppercase tracking-[0.14em] text-white/50 mb-0.5">
              Бонусний рахунок
            </div>
            <div className="font-display text-[1.5rem] font-bold leading-none">
              {formatAmount(data.regular_balance_uah)}{" "}
              <span className="font-serif italic text-primary text-[1.125rem]">
                ₴
              </span>
            </div>
            <div className="mt-1 text-[0.6875rem] text-white/60">
              До {MAX_BONUS_PERCENT}% від вартості підписки можна оплатити бонусами
            </div>
            {data.regular_balance_uah > 0 && data.regular_expires_at && (
              <div className="mt-1 text-[0.6875rem] font-semibold text-primary">
                Дійсні до{" "}
                {new Date(data.regular_expires_at).toLocaleDateString("uk-UA", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}
              </div>
            )}
          </div>
          <div className="relative grid h-9 w-9 place-items-center rounded-full bg-primary">
            <Icon name="arrow-right" size={16} color="#1a0c02" />
          </div>
        </Link>
      </div>

      {/* Stats row — третій блок ширший, щоб вмістити «Можна заробити»
          (юзер 2026-06-11: «Доступно» незрозуміло — це не баланс, а скільки
          ще можна заробити). Виконано/прогрес — вужчі. */}
      <div className="px-5 pt-3 grid grid-cols-[0.8fr_0.8fr_1.4fr] gap-2.5">
        <StatTile label="Виконано" value={`${done}/${total}`} />
        <ProgressLiquidTile pct={pct} />
        <StatTile
          label="Можна заробити"
          value={`+${formatAmount(remainingReward)} ₴`}
        />
      </div>

      {/* Group sections */}
      <div className="pt-2">
        {GROUP_ORDER.map((g) => {
          const list = groups[g];
          if (!list || list.length === 0) return null;
          const meta = GROUP_META[g];
          return (
            <div key={g} className="px-5 pt-7">
              <div className="mb-3 flex items-center gap-2.5">
                <div className="grid h-7 w-7 place-items-center rounded-full bg-primary/10 text-primary">
                  <Icon name={meta.icon} size={14} />
                </div>
                <div>
                  <div className="font-display text-[0.75rem] font-semibold uppercase tracking-[0.08em] text-ink">
                    {meta.title}
                  </div>
                  <div className="text-[0.6875rem] text-ink-dim">
                    {meta.subtitle}
                  </div>
                </div>
              </div>
              <div className="space-y-2.5">
                {list.map((q) => (
                  <QuestCard key={q.id} q={q} groupIcon={meta.icon} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Підрозділ «Підписки на канали» — 4 квести по 50₴ (owner 2026-07-16) */}
      <SubscriptionQuestsSection />
    </div>
  );
}

// ============================================================
// SubscriptionQuestsSection — підписка на канали/чат inFLOW
// ============================================================

function SubscriptionQuestsSection() {
  const query = useSubscriptionQuests();
  const [selected, setSelected] = useState<SubscriptionQuestItemOut | null>(
    null,
  );

  if (query.isLoading || query.isError || !query.data) return null;
  const { items, total_reward_uah, earned_reward_uah } = query.data;
  if (items.length === 0) return null;

  return (
    <div className="px-5 pt-7">
      <div className="mb-3 flex items-center gap-2.5">
        <div className="grid h-7 w-7 place-items-center rounded-full bg-primary/10 text-primary">
          <Icon name="bell" size={14} />
        </div>
        <div className="flex-1">
          <div className="font-display text-[0.75rem] font-semibold uppercase tracking-[0.08em] text-ink">
            Підписки на канали
          </div>
          <div className="text-[0.6875rem] text-ink-dim">
            +50 ₴ за кожну · до {formatAmount(total_reward_uah)} ₴ разом
          </div>
        </div>
        <div className="shrink-0 font-mono text-[0.6875rem] font-bold text-primary">
          {formatAmount(earned_reward_uah)}/{formatAmount(total_reward_uah)} ₴
        </div>
      </div>

      <div className="space-y-2.5">
        {items.map((it) => (
          <SubQuestCard key={it.key} item={it} onOpen={() => setSelected(it)} />
        ))}
      </div>

      {selected && (
        <SubscriptionQuestModal
          item={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

function SubQuestCard({
  item,
  onOpen,
}: {
  item: SubscriptionQuestItemOut;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={
        "block w-full text-left rounded-ios-card p-4 transition active:scale-[0.99] " +
        (item.is_done
          ? "bg-primary/[0.06] border border-primary/20"
          : "bg-surface shadow-card")
      }
    >
      <div className="flex items-center gap-3">
        <div className="relative shrink-0">
          <img
            src={item.avatar_url}
            alt={item.title}
            className="h-10 w-10 rounded-[0.75rem] object-cover ring-1 ring-black/5"
            draggable={false}
          />
          {item.is_done && (
            <div className="absolute -bottom-1 -right-1 grid h-4 w-4 place-items-center rounded-full bg-primary ring-2 ring-surface">
              <Icon name="check" size={10} color="#FFFFFF" />
            </div>
          )}
        </div>

        {/* Текст — назва + опис/статус */}
        <div className="flex-1 min-w-0">
          <div className="font-display text-[0.875rem] font-semibold text-ink leading-snug truncate">
            {item.title}
          </div>
          <div className="mt-0.5 text-[0.75rem] text-ink-dim leading-relaxed">
            {item.is_done ? "Виконано · бонус нараховано" : item.caption}
          </div>
        </div>

        {/* Фіксована права колонка: число ЗАВЖДИ на однаковому місці,
            стрілка — під ним (owner 2026-07-16: єдиний стиль). */}
        <div className="flex shrink-0 flex-col items-end gap-1">
          <div
            className={
              "font-mono text-[0.75rem] font-bold whitespace-nowrap " +
              (item.is_done ? "text-primary" : "text-ink")
            }
          >
            +{formatAmount(item.reward_uah)} ₴
          </div>
          {!item.is_done && (
            <Icon name="arrow-right" size={14} />
          )}
        </div>
      </div>
    </button>
  );
}

// ============================================================
// StatTile — маленька квадратна-ish card для stat-row
// ============================================================

function StatTile({
  label,
  value,
  accent = false,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div className="rounded-ios-card bg-surface px-3 py-3.5 shadow-card-soft min-h-[4.25rem]">
      <div className="font-mono text-[0.65625rem] uppercase tracking-[0.1em] text-ink-dim mb-1">
        {label}
      </div>
      <div
        className={
          "font-display text-[1.0625rem] font-bold leading-tight whitespace-nowrap " +
          (accent ? "text-primary" : "text-ink")
        }
      >
        {value}
      </div>
    </div>
  );
}

// ============================================================
// ProgressLiquidTile — як «батарея»: помаранчева рідина заповнює
// плашку знизу вгору пропорційно %. SVG-хвилі зверху рідини
// створюють ефект liquid.
// ============================================================

function ProgressLiquidTile({ pct }: { pct: number }) {
  const fillHeight = Math.max(0, Math.min(100, pct));
  return (
    <div className="relative overflow-hidden rounded-ios-card bg-surface px-3 py-3.5 shadow-card-soft min-h-[4.25rem]">
      {/* Liquid fill — primary, заповнює знизу */}
      <div
        className="absolute inset-x-0 bottom-0 transition-all duration-1000 ease-out"
        style={{
          height: `${fillHeight}%`,
          background:
            "linear-gradient(180deg, rgba(254,107,4,0.32) 0%, rgba(254,107,4,0.55) 100%)",
        }}
      >
        {/* Wavey top edge — окремий SVG-рядок поверх рідини */}
        <svg
          className="absolute top-0 left-0 right-0 w-full"
          height="6"
          viewBox="0 0 100 6"
          preserveAspectRatio="none"
          style={{ transform: "translateY(-99%)" }}
        >
          <path
            d="M0,6 Q12.5,0 25,3 T50,3 T75,3 T100,3 L100,6 L0,6 Z"
            fill="rgba(254,107,4,0.55)"
          >
            <animate
              attributeName="d"
              dur="3s"
              repeatCount="indefinite"
              values="
                M0,6 Q12.5,0 25,3 T50,3 T75,3 T100,3 L100,6 L0,6 Z;
                M0,6 Q12.5,4 25,1 T50,1 T75,1 T100,1 L100,6 L0,6 Z;
                M0,6 Q12.5,0 25,3 T50,3 T75,3 T100,3 L100,6 L0,6 Z
              "
            />
          </path>
        </svg>
        {/* Bubble shimmer */}
        {fillHeight > 15 && (
          <div className="absolute inset-0 opacity-40 pointer-events-none">
            <span
              className="absolute h-1 w-1 rounded-full bg-white animate-pulse"
              style={{ left: "20%", bottom: "20%", animationDelay: "0.4s" }}
            />
            <span
              className="absolute h-0.5 w-0.5 rounded-full bg-white animate-pulse"
              style={{ left: "60%", bottom: "45%", animationDelay: "1.2s" }}
            />
          </div>
        )}
      </div>

      {/* Foreground content */}
      <div className="relative z-10">
        <div className="font-mono text-[0.65625rem] uppercase tracking-[0.1em] text-ink-dim mb-1">
          Прогрес
        </div>
        <div
          className={
            "font-display text-[1.0625rem] font-bold leading-tight " +
            (pct > 0 ? "text-primary" : "text-ink")
          }
        >
          {pct}%
        </div>
      </div>
    </div>
  );
}
